import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { Building2, ArrowRight, Sparkles, Check } from "lucide-react";

export default function SharePublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/share/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Share not found."));
  }, [slug]);

  // Set document title and meta description for browsers (crawlers use /api/share/og/:slug).
  useEffect(() => {
    if (!data) return;
    const who = data.business_name || `${data.first_name}'s business`;
    const amt = `$${Math.round(data.tax_saved).toLocaleString()}`;
    const title = `${who} saved ${amt} in ${data.tax_year} business taxes`;
    const desc = `${data.deduction_count} deductions tracked · ${amt} saved with BizCashflow Pros.`;
    const prev = document.title;
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (metaDesc) metaDesc.setAttribute("content", desc);
    return () => {
      document.title = prev;
      if (metaDesc && prevDesc != null) metaDesc.setAttribute("content", prevDesc);
    };
  }, [data]);

  const registerHref = `/register?ref=${encodeURIComponent(slug)}`;

  if (err) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-6" data-testid="share-not-found">
        <div className="text-center max-w-sm">
          <h1 className="font-heading text-3xl font-medium mb-3">Link expired or revoked</h1>
          <p className="text-ink-light mb-8">{err}</p>
          <Link to="/" className="btn-forest inline-flex items-center gap-2">
            Visit BizCashflow Pros <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-paper flex items-center justify-center text-ink-lighter">Loading…</div>;
  }

  const owner = data.business_name || `${data.first_name}'s business`;

  return (
    <div className="min-h-screen bg-paper" data-testid="share-public-page">
      <header className="border-b border-border bg-paper/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-forest flex items-center justify-center text-white">
              <Building2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <span className="font-heading text-xl font-semibold">BizCashflow <span className="text-forest">Pros</span></span>
          </Link>
          <Link to={registerHref} className="btn-forest text-sm" data-testid="share-cta-signup">
            Find yours <ArrowRight className="w-4 h-4 inline-block ml-1" />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="overline mb-5">A real tax win, shared by {data.first_name}</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] mb-6">
          <span className="italic font-normal">{owner}</span> kept<br />
          <span className="text-forest num">{fmtMoney(data.tax_saved)}</span> in {data.tax_year} taxes.
        </h1>
        <p className="text-lg text-ink-light max-w-2xl leading-relaxed mb-12">
          That's real money — tracked, deducted, and saved by using BizCashflow Pros to log every business write-off.
          Most owners leave thousands like this on the table.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mb-12" data-testid="share-stats">
          <div className="card-surface p-6">
            <div className="overline mb-2">Tax saved</div>
            <div className="num font-heading text-4xl font-medium text-forest">{fmtMoney(data.tax_saved)}</div>
            <div className="text-xs text-ink-lighter mt-2">Year {data.tax_year}</div>
          </div>
          <div className="card-surface p-6">
            <div className="overline mb-2">Deductions tracked</div>
            <div className="num font-heading text-4xl font-medium">{data.deduction_count}</div>
            <div className="text-xs text-ink-lighter mt-2">Individual write-offs</div>
          </div>
          <div className="card-surface p-6">
            <div className="overline mb-2">Total deducted</div>
            <div className="num font-heading text-4xl font-medium">{fmtMoney(data.deductible_expenses)}</div>
            <div className="text-xs text-ink-lighter mt-2">From taxable income</div>
          </div>
        </div>

        {data.top_categories.length > 0 && (
          <div className="card-surface p-6 mb-12">
            <div className="overline mb-1.5">Top categories</div>
            <h2 className="font-heading text-2xl font-medium mb-5">Where the savings came from</h2>
            <div className="space-y-3">
              {data.top_categories.map((c) => (
                <div key={c.category} className="flex items-center justify-between text-sm" data-testid={`share-cat-${c.category}`}>
                  <div className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-forest" strokeWidth={2} />
                    <span>{c.category}</span>
                  </div>
                  <span className="num text-ink-light">{fmtMoney(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-forest text-white rounded-2xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none"></div>
          <div className="relative max-w-2xl">
            <Sparkles className="w-6 h-6 mb-5" strokeWidth={1.5} />
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-4 leading-tight">
              How much are <span className="italic font-normal">you</span> leaving on the table?
            </h2>
            <p className="text-forest-50 text-base mb-8 leading-relaxed">
              The average BizCashflow Pros user finds <span className="num text-white font-medium">$4,800</span> in
              missed deductions in their first month. Sign up through {data.first_name}'s link and
              <span className="font-medium text-white"> {data.first_name} gets a free month</span> as a thank-you.
            </p>
            <Link to={registerHref} className="inline-flex items-center gap-2 bg-white text-forest hover:bg-forest-50 px-7 py-3 rounded-lg font-medium" data-testid="share-cta-bottom">
              Find my deductions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 text-xs text-ink-lighter flex justify-between flex-col md:flex-row gap-3">
          <div>Shared by {data.first_name} via BizCashflow Pros · {data.view_count.toLocaleString()} views</div>
          <div>BizCashflow Pros is not a tax advisor. Numbers reflect user-tracked deductions only.</div>
        </div>
      </footer>
    </div>
  );
}
